import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { LoadingIndicator } from 'components/common';
import { PageEmptyIndicator, PageSectionHeader } from 'components/page';
import {
  AchievementsViewAdministered,
  AchievementsViewAllAchievements,
  AchievementsViewAvailable,
  AchievementsViewUser
} from '.';

import type { ViewProps } from 'types';

const AchievementsViewSearch = ({ queryParams }: ViewProps) => {
  const { messages, user } = useReduxState();

  const router = useRouter();

  const [loadingAdmin, setLoadingAdmin] = useState(true);
  const [loadingAvailable, setLoadingAvailable] = useState(true);
  const [loadingUser, setLoadingUser] = useState(true);
  const [showAdministed, setShowAdministered] = useState(false);
  const [showAvailable, setShowAvailable] = useState(false);
  const [showUser, setShowUser] = useState(false);

  useEffect(() => {
    if (!loadingAdmin || !loadingAvailable || !loadingUser) {
      setLoadingAdmin(true);
      setLoadingAvailable(true);
      setLoadingUser(true);
    }
  }, [queryParams]);

  const handleNoAdminItems = (hasData: boolean) => {
    setShowAdministered(hasData);
    setLoadingAdmin(false);
  };

  const handleNoUserItems = (hasData: boolean) => {
    setShowUser(hasData);
    setLoadingUser(false);
  };

  const handleNoAvailableItems = (hasData: boolean) => {
    setShowAvailable(hasData);
    setLoadingAvailable(false);
  };

  const isAdmin = user?.roles.includes('admin');
  const isSuperadmin = user?.roles.includes('superadmin');

  const loading =
    ((isAdmin || isSuperadmin) && loadingAdmin) ||
    loadingAvailable ||
    loadingUser;

  return (
    <>
      <PageSectionHeader
        title={`${messages.commonSearch}: ${router.query?.search}`}
      />

      {loading ? <LoadingIndicator /> : null}

      <div className="search">
        {isSuperadmin ? (
          <div className="search-result">
            <AchievementsViewAllAchievements
              hideIfNoData={true}
              limitPerPage={12}
              onNoData={handleNoAdminItems}
              queryParams={queryParams}
            />
          </div>
        ) : isAdmin ? (
          <div className="search-result">
            <AchievementsViewAdministered
              hideIfNoData={true}
              limitPerPage={12}
              onNoData={handleNoAdminItems}
              queryParams={queryParams}
            />
          </div>
        ) : null}

        <div className="search-result">
          <AchievementsViewUser
            hideIfNoData={true}
            limitPerPage={12}
            onNoData={handleNoUserItems}
            queryParams={queryParams}
          />
        </div>

        <div className="search-result">
          <AchievementsViewAvailable
            hideIfNoData={true}
            limitPerPage={12}
            onNoData={handleNoAvailableItems}
            queryParams={queryParams}
          />
        </div>
      </div>

      {!showAdministed && !showAvailable && !showUser ? (
        <PageEmptyIndicator
          icon="check-circle"
          title={messages.achievementsEmpty}
          description={messages.achievementsEmpty}
        />
      ) : null}

      <style jsx>{`
        .search {
          display: ${loading ? 'none' : 'flex'};
          flex-direction: column;
        }
        .search-result:not(:first-child) {
          margin-top: 50px;
        }
      `}</style>
    </>
  );
};

export default AchievementsViewSearch;
